import { Component, ChangeDetectionStrategy } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { getMyApp } from 'app/app-interface';
import { getDeviceId, getRootDomain, isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CsPage, NavController } from 'providers/nav-controller';
import { MenuController } from 'providers/menu-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { LoggingService } from 'services/logging.service';
import { FatalErrorReport } from 'services/fatal-error-report';

@Component({
    selector: 'page-fatal-error',
    templateUrl: 'fatal-error.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 600, height: 400 }) }]
})
export class FatalErrorPage extends CsPage<{}> {
  protected LL = LL;
  titleText = "Whoops...";
  messageText = '';
  errorHelpText = 'Please try again later.';
  showRetry = true;
  showAddress = false;
  showLogout = false;
  /** #4401: offer to start this device's local copy over. Only for a storage jam the app
   *  can actually escape — see FatalErrorPageParams.canResetLocalStore. */
  showResetLocalData = false;
  /** #4401: the sentence explaining what starting over costs, shown beside that button. */
  resetLocalDataOfferText = '';
  mainUrl = '';

  constructor(
    public navCtrl: NavController,
    private menuCtrl: MenuController,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    
  ) {
    super();
    // Set default texts with translation
    this.titleText = LL.fatalError.title.whoops();
    this.errorHelpText = LL.fatalError.help.tryAgainLater();

    // this happens when the app is reloaded into the fatal error page - we will redirect in ionViewWillEnter
    if (!getMyApp().fatalErrorParams) {
      return;
    }

    let type = getMyApp().fatalErrorParams.type;
    if (type == 'database_recovery_error') {
      this.titleText = LL.fatalError.title.whoops();
      this.messageText = LL.fatalError.messages.cantAccessData();
      this.errorHelpText = LL.fatalError.help.retryOrLogout();
      this.showLogout = true;
      this.showRetry = true;

    } else if (type == 'test_fatal_error') {
      this.titleText = LL.fatalError.title.testError();
      this.messageText = LL.fatalError.messages.testError();
      this.errorHelpText = LL.fatalError.help.testError();
      this.showRetry = true;

    } else if (type == 'booting_error') {
      this.titleText = LL.fatalError.title.whoops();
      this.messageText = LL.fatalError.messages.appFailedToStart();
      this.errorHelpText = LL.fatalError.help.retryOrRelogin();
      this.showRetry = true;
      this.showLogout = true;
    } else if (type == 'lockdown_mode_error') {
      this.titleText = LL.fatalError.title.whoops();
      this.messageText = LL.fatalError.messages.lockdownMode();
      this.errorHelpText = LL.fatalError.help.disableLockdown();
      this.showRetry = true;
      this.showAddress = false;
    } else if (type == 'local_storage_wedged') {
      // #4401: the device's own storage accepted the app's reads and answered none of
      // them, and reloading has already been tried as often as it is going to be. What
      // matters most here is that the advice is CORRECT for the platform: closing a tab
      // does not clear this on iOS, and telling somebody to do that sends them to do
      // something that cannot work.
      this.titleText = LL.fatalError.title.storageNotResponding();
      this.messageText = LL.fatalError.messages.localStorageWedged();
      this.errorHelpText = isCapacitorNative()
        ? LL.fatalError.help.storageRestartApp()
        : (isPlatformIOS() ? LL.fatalError.help.storageForceQuitIos() : LL.fatalError.help.storageRestartBrowser());
      this.showRetry = true;
      this.showResetLocalData = getMyApp().fatalErrorParams?.canResetLocalStore === true;
      if (this.showResetLocalData) {
        this.resetLocalDataOfferText = LL.fatalError.messages.resetLocalDataOffer();
      }
    } else if (type == 'server_error') {
      this.titleText = LL.fatalError.title.whoops();
      this.messageText = LL.fatalError.messages.serverError();
      this.errorHelpText = LL.fatalError.help.tryAgainLater();
      this.showRetry = true;
    }

    if (this.showAddress) {
      // show the address of the app
      this.mainUrl = getRootDomain();

      // get the root of the web (without the path)
      let adjustedUrl = new URL(window.location.href);
      adjustedUrl.pathname = '';
      // add the device id to the url
      adjustedUrl.searchParams.set('utm_clid', getDeviceId());
      // and set that as the actual url of this page, so that when the user opens in the browser, it will have all the tracking data
      history.replaceState(null, '', adjustedUrl.toString());
    }
  }

  async ionViewCanEnter() {
    // (this happens when the app is reloaded into the fatal error page)
    if (!getMyApp().fatalErrorParams) {
      console.log('FatalErrorPage: no error params');
      // reload to see the current status (i.e. rechecks the error)
      await getMyApp().reloadAppAsync({ resetToRoot: true });
      return false;
    }
    return true;
  }

  async ionViewWillEnter() {
    this.menuCtrl.enable(false);
    this.logging.trackFatalErrorScreen(FatalErrorReport.Shown, getMyApp().fatalErrorParams);
  }

  async ionViewWillLeave() {
    this.menuCtrl.enable(true);
  }

  async retry() {
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  /**
   * Start this device's local copy over (#4401).
   *
   * Asks first, because it can cost the user anything this device had not managed to sync
   * yet — and the alert says so in those words rather than in ours.
   */
  async resetLocalData() {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.fatalError.alerts.resetLocalDataTitle(),
      message: LL.fatalError.alerts.resetLocalDataMessage(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.fatalError.buttons.resetLocalData(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;
    await getMyApp().resetLocalStoreAndReloadAsync();
  }

  async logout() {
    // note that we do NOT call app.logoutAsync() here, because we might not be logged in correctly yet! but we do want to make sure we get a clean slate
    await getMyApp().reloadAppAsync({ resetToRoot: true, clearLocalData: true });
  }

  fakeLinkClicked() {
    // copy the current page to the clipboard
    let el = document.createElement('textarea');
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    // tell the user to paste it
    void this.alertCtrl.showAlertAsync({
      message: LL.common.linkCopied()
    });
  }
}
